<template>
  <div class="m-orders">
    <van-nav-bar
      title="我的订单"
      left-arrow
      @click-left="$router.back()"
      fixed
      placeholder
    />

    <!-- 骨架屏加载 -->
    <div v-if="loading" class="m-orders__skeleton">
      <div v-for="n in 4" :key="n" class="m-orders__skeleton-card">
        <div class="m-orders__skeleton-header">
          <div class="m-orders__skeleton-no"></div>
          <div class="m-orders__skeleton-tag"></div>
        </div>
        <div class="m-orders__skeleton-product"></div>
        <div class="m-orders__skeleton-price"></div>
        <div class="m-orders__skeleton-time"></div>
        <div class="m-orders__skeleton-actions">
          <div class="m-orders__skeleton-btn"></div>
          <div class="m-orders__skeleton-btn"></div>
        </div>
      </div>
    </div>

    <div v-else-if="orderList.length > 0" class="m-orders__list">
      <div
        v-for="item in orderList"
        :key="item.orderId"
        class="m-orders__card"
      >
        <!-- 卡片头部 -->
        <div class="m-orders__card-header">
          <span class="m-orders__card-no">订单号：{{ item.orderNo }}</span>
          <van-tag :type="payTagType(item.payStatus)" size="medium">{{ payStatusText(item.payStatus) }}</van-tag>
        </div>

        <!-- 卡片内容 -->
        <div class="m-orders__card-body">
          <div class="m-orders__card-product">{{ formatProductName(item) }}</div>
          <div class="m-orders__card-price">¥{{ item.orderAmount }}</div>
          <div class="m-orders__card-time">下单时间：{{ item.createTime || '-' }}</div>
        </div>

        <!-- 卡片操作 -->
        <div class="m-orders__card-actions">
          <!-- 待支付：扫码支付 -->
          <van-button
            v-if="item.payStatus === 0"
            type="warning"
            size="small"
            round
            @click="openPayDialog(item)"
          >去支付</van-button>

          <!-- 已支付：发票操作 -->
          <template v-if="item.payStatus === 1">
            <van-tag plain :type="invoiceTagType(item.invoiceApplyStatus)" size="medium">
              {{ invoiceStatusText(item.invoiceApplyStatus) }}
            </van-tag>

            <!-- 申请发票 -->
            <van-button
              v-if="item.invoiceApplyStatus === null || item.invoiceApplyStatus === undefined"
              type="primary"
              size="small"
              round
              @click="openApplyInvoice(item)"
            >申请发票</van-button>

            <!-- 修改发票 -->
            <van-button
              v-if="[0, 2, 3].includes(item.invoiceApplyStatus)"
              plain
              type="primary"
              size="small"
              round
              @click="openEditInvoice(item)"
            >修改发票</van-button>

            <!-- 查看发票 -->
            <van-button
              v-if="item.invoiceApplyStatus === 1"
              plain
              type="info"
              size="small"
              round
              @click="viewInvoice(item)"
            >查看发票</van-button>
          </template>
        </div>
      </div>
    </div>

    <van-empty v-else description="暂无订单" />

    <!-- 发票弹窗 -->
    <van-popup
      v-model="showInvoicePopup"
      position="bottom"
      round
      closeable
      :style="{ maxHeight: '80%' }"
    >
      <div class="m-orders__invoice">
        <div class="m-orders__invoice-title">{{ invoicePopupTitle }}</div>

        <van-cell-group>
          <van-cell title="发票类型" value="电子普通发票" />
          <van-cell title="发票内容" value="商品明细" />
        </van-cell-group>

        <div class="m-orders__invoice-section">
          <div class="m-orders__invoice-label">发票抬头</div>
          <van-radio-group v-model="invoiceForm.titleType" direction="horizontal" class="m-orders__radio-group">
            <van-radio :name="0">个人</van-radio>
            <van-radio :name="1">单位</van-radio>
          </van-radio-group>
        </div>

        <!-- 个人 -->
        <template v-if="invoiceForm.titleType === 0">
          <van-field
            v-model="invoiceForm.personalTitle"
            label="发票抬头"
            placeholder="请输入个人姓名"
          />
          <van-field
            v-model="invoiceForm.personalPhone"
            label="电话号码"
            placeholder="请输入电话号码"
            type="tel"
          />
        </template>

        <!-- 单位 -->
        <template v-if="invoiceForm.titleType === 1">
          <van-field
            v-model="invoiceForm.companyName"
            label="单位名称"
            placeholder="请输入单位名称"
          />
          <van-field
            v-model="invoiceForm.taxNo"
            label="信用代码"
            placeholder="请输入统一社会信用代码"
          />
          <van-field
            v-model="invoiceForm.companyPhone"
            label="单位电话"
            placeholder="请输入单位电话"
            type="tel"
          />
        </template>

        <!-- 驳回信息 -->
        <template v-if="invoiceDetail && invoiceDetail.status === 2">
          <div class="m-orders__invoice-audit">
            <div>拒绝原因：{{ invoiceDetail.auditRemark || '无' }}</div>
            <div>审核时间：{{ invoiceDetail.auditTime || '无' }}</div>
          </div>
        </template>

        <div class="m-orders__invoice-actions">
          <van-button block type="primary" round :loading="submitting" @click="submitInvoice">提交</van-button>
          <van-button
            v-if="invoicePopupMode === 'edit' && invoiceDetail && [0, 2].includes(invoiceDetail.status)"
            block
            round
            @click="cancelInvoiceApply"
            style="margin-top: 8px"
          >取消申请</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 支付确认弹窗 -->
    <van-popup v-model="showPayDialog" round closeable :style="{ width: '80%' }">
      <div class="m-orders__pay">
        <div class="m-orders__pay-title">确认支付</div>
        <div v-if="currentPayOrder" class="m-orders__pay-info">
          <div class="m-orders__pay-product">{{ formatProductName(currentPayOrder) }}</div>
          <div class="m-orders__pay-amount">¥{{ currentPayOrder.orderAmount }}</div>
          <div class="m-orders__pay-no">订单号：{{ currentPayOrder.orderNo }}</div>
        </div>
        <van-button
          type="danger"
          block
          round
          :loading="paying"
          @click="onPayment"
        >{{ paying ? '支付中...' : '确认支付' }}</van-button>
      </div>
    </van-popup>

    <!-- 支付成功提示 -->
    <van-popup v-model="showPaySuccess" round :style="{ width: '70%' }">
      <div class="m-orders__pay">
        <div class="m-orders__pay-title">支付成功</div>
        <div class="m-orders__pay-success">
          <van-icon name="passed" size="48" color="#07c160" />
          <p>订单支付成功</p>
        </div>
        <van-button type="primary" block round @click="closePaySuccess">确定</van-button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Toast } from 'vant'
import mobileViewport from './mixins/viewport'

/* global WeixinJSBridge */
import {
  getOrderList,
  getInvoiceApplyDetail,
  applyInvoice,
  updateInvoice,
  cancelInvoiceApply,
  wxUnifiedOrder,
  getPayStatus
} from '../../api/order'

export default {
  mixins: [mobileViewport],
  name: 'MobileOrders',
  data() {
    return {
      orderList: [],
      pageNum: 1,
      pageSize: 10,
      loading: false,
      showInvoicePopup: false,
      invoicePopupMode: 'apply',
      currentOrder: null,
      invoiceForm: {
        titleType: 0,
        personalTitle: '',
        personalPhone: '',
        companyName: '',
        taxNo: '',
        companyPhone: ''
      },
      invoiceDetail: null,
      submitting: false,
      showPayDialog: false,
      currentPayOrder: null,
      paying: false,
      showPaySuccess: false,
      paySuccessOrderNo: ''
    }
  },
  computed: {
    invoicePopupTitle() {
      return this.invoicePopupMode === 'apply' ? '申请发票' : '发票信息'
    }
  },
  mounted() {
    this.getOrderList()
  },
  methods: {
    payStatusText(status) {
      const map = { 0: '待支付', 1: '支付成功', 2: '支付失败', 3: '已取消' }
      return map[status] || '未知'
    },
    formatProductName(item) {
      const periodTypeMap = { 1: '单期', 2: '年度订阅' }
      const mediaTypeMap = { 1: '电子版', 2: '纸质版' }
      const periodTypeText = periodTypeMap[item.periodType] || ''
      const mediaTypeText = mediaTypeMap[item.mediaType] || ''
      const year = item.year || ''

      if (item.periodType === 2) {
        return `${year}年${periodTypeText}${mediaTypeText}`
      }

      const issueNumber = item.issueNumber
      const issueText = issueNumber !== undefined && issueNumber !== null
        ? (parseInt(issueNumber, 10) < 10 ? `0${issueNumber}` : issueNumber)
        : ''
      return `${year}年第${issueText}期${mediaTypeText}`
    },
    payTagType(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'default' }
      return map[status] || 'default'
    },
    invoiceStatusText(status) {
      if (status === null || status === undefined) return '发票未申请'
      const map = { 0: '发票待审核', 1: '发票已开票', 2: '发票已拒绝', 3: '发票已取消' }
      return map[status] || '发票未申请'
    },
    invoiceTagType(status) {
      if (status === null || status === undefined) return 'default'
      const map = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'default' }
      return map[status] || 'default'
    },
    async getOrderList() {
      this.loading = true
      try {
        const res = await getOrderList({
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.orderList = data.records || []
      } catch (err) {
        Toast(err.message || '获取订单列表失败')
      } finally {
        this.loading = false
      }
    },
    openPayDialog(order) {
      this.currentPayOrder = order
      this.showPayDialog = true
      this.paying = false
    },
    closePayDialog() {
      this.showPayDialog = false
      this.currentPayOrder = null
      this.paying = false
    },
    async onPayment() {
      if (!this.currentPayOrder || this.paying) return
      this.paying = true
      try {
        await this.initWechatPay(this.currentPayOrder.orderNo)
      } catch (err) {
        console.error('支付失败:', err)
        Toast(err.message || '支付失败，请稍后重试')
      } finally {
        this.paying = false
      }
    },

    // 调起微信支付（微信公众号 H5 使用 WeixinJSBridge）
    initWechatPay(orderNo) {
      return new Promise((resolve, reject) => {
        wxUnifiedOrder({ orderNo, type: 'h5' })
          .then(res => {
            // request.js 已统一处理 code，res 直接是后端返回的 JSON 对象
            if (res && res.code === 200) {
              const wxData = res.data
              if (!wxData.timeStamp || !wxData.package || !wxData.signType || !wxData.nonceStr || !wxData.paySign) {
                reject(new Error('微信下单数据不完整'))
                return
              }

              const appId = wxData.appId || wxData.appid || process.env.VUE_APP_WECHAT_APPID || ''
              if (!appId) {
                reject(new Error('缺少微信公众号 AppID'))
                return
              }

              const payParams = {
                appId: appId,
                timeStamp: wxData.timeStamp,
                nonceStr: wxData.nonceStr,
                package: wxData.package,
                signType: wxData.signType || 'MD5',
                paySign: wxData.paySign
              }

              const callWechatPay = () => {
                WeixinJSBridge.invoke(
                  'getBrandWCPayRequest',
                  payParams,
                  res => {
                    if (res.err_msg === 'get_brand_wcpay_request:ok') {
                      this.startPayStatusCheck(orderNo)
                      resolve()
                    } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
                      reject(new Error('微信支付被取消'))
                    } else {
                      console.error('微信支付失败:', res.err_msg)
                      reject(new Error('微信支付失败'))
                    }
                  }
                )
              }

              if (typeof WeixinJSBridge === 'undefined') {
                if (document.addEventListener) {
                  document.addEventListener('WeixinJSBridgeReady', callWechatPay, false)
                } else if (document.attachEvent) {
                  document.attachEvent('WeixinJSBridgeReady', callWechatPay)
                  document.attachEvent('onWeixinJSBridgeReady', callWechatPay)
                }
              } else {
                callWechatPay()
              }
            } else {
              reject(new Error(res.msg || '微信下单失败'))
            }
          })
          .catch(reject)
      })
    },

    // 轮询查询支付状态
    startPayStatusCheck(orderNo) {
      this.paySuccessOrderNo = orderNo
      this.showPaySuccess = false

      const checkInterval = setInterval(() => {
        getPayStatus({ orderNo })
          .then(res => {
            // request.js 已统一处理 code，res 直接是后端返回的 JSON 对象
            if (res && res.code === 200) {
              const payStatus = res.data && res.data.payStatus
              if (payStatus === 1) {
                clearInterval(checkInterval)
                this.showPayDialog = false
                this.showPaySuccess = true
                this.getOrderList()
              }
            }
          })
          .catch(err => {
            console.error('查询支付状态失败:', err)
          })
      }, 1000)
    },
    closePaySuccess() {
      this.showPaySuccess = false
      this.paySuccessOrderNo = ''
    },
    resetInvoiceForm() {
      this.invoiceForm = {
        titleType: 0,
        personalTitle: '',
        personalPhone: '',
        companyName: '',
        taxNo: '',
        companyPhone: ''
      }
    },
    openApplyInvoice(order) {
      this.invoicePopupMode = 'apply'
      this.currentOrder = order
      this.invoiceDetail = null
      this.resetInvoiceForm()
      this.showInvoicePopup = true
    },
    async openEditInvoice(order) {
      this.invoicePopupMode = 'edit'
      this.currentOrder = order
      this.invoiceDetail = null
      this.resetInvoiceForm()
      this.showInvoicePopup = true
      try {
        const res = await getInvoiceApplyDetail({
          id: order.invoiceApplyId
        })
        const detail = res.data || {}
        this.invoiceDetail = detail
        this.invoiceForm = {
          titleType: detail.titleType !== undefined ? detail.titleType : 0,
          personalTitle: detail.personalTitle || '',
          personalPhone: detail.personalPhone || '',
          companyName: detail.companyName || '',
          taxNo: detail.taxNo || '',
          companyPhone: detail.companyPhone || ''
        }
      } catch (err) {
        Toast(err.message || '获取发票信息失败')
        this.showInvoicePopup = false
      }
    },
    viewInvoice(order) {
      if (order.invoiceUrl) {
        window.open(order.invoiceUrl, '_blank')
      } else {
        Toast('暂无发票文件')
      }
    },
    validateForm() {
      if (this.invoiceForm.titleType === 0) {
        if (!this.invoiceForm.personalTitle) { Toast('请输入发票抬头'); return false }
        if (!this.invoiceForm.personalPhone) { Toast('请输入电话号码'); return false }
      } else {
        if (!this.invoiceForm.companyName) { Toast('请输入单位名称'); return false }
        if (!this.invoiceForm.taxNo) { Toast('请输入统一社会信用代码'); return false }
        if (!this.invoiceForm.companyPhone) { Toast('请输入单位电话'); return false }
      }
      return true
    },
    buildFormParams() {
      const params = { titleType: this.invoiceForm.titleType }
      if (this.invoiceForm.titleType === 0) {
        params.personalTitle = this.invoiceForm.personalTitle
        params.personalPhone = this.invoiceForm.personalPhone
      } else {
        params.companyName = this.invoiceForm.companyName
        params.taxNo = this.invoiceForm.taxNo
        params.companyPhone = this.invoiceForm.companyPhone
      }
      return params
    },
    async submitInvoice() {
      if (!this.validateForm()) return
      if (this.submitting) return
      this.submitting = true
      try {
        if (this.invoicePopupMode === 'apply') {
          await applyInvoice({
            ...this.buildFormParams(),
            orderNo: this.currentOrder.orderNo
          })
          Toast('申请成功')
        } else {
          await updateInvoice({
            ...this.buildFormParams(),
            id: this.currentOrder.invoiceApplyId
          })
          Toast('修改成功')
        }
        this.showInvoicePopup = false
        this.getOrderList()
      } catch (err) {
        Toast(err.message || '操作失败')
      } finally {
        this.submitting = false
      }
    },
    async cancelInvoiceApply() {
      if (this.submitting) return
      this.submitting = true
      try {
        await cancelInvoiceApply({
          id: this.currentOrder.invoiceApplyId
        })
        Toast('取消申请成功')
        this.showInvoicePopup = false
        this.getOrderList()
      } catch (err) {
        Toast(err.message || '取消失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.m-orders {
  min-height: 100vh;
  background: #F5F7FA;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ====== 骨架屏 ====== */
.m-orders__skeleton {
  padding: 12px;
}

.m-orders__skeleton-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.m-orders__skeleton-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.m-orders__skeleton-no {
  width: 140px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-orders__skeleton-tag {
  width: 52px;
  height: 18px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-orders__skeleton-product {
  width: 60%;
  height: 18px;
  border-radius: 4px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-orders__skeleton-price {
  width: 80px;
  height: 24px;
  border-radius: 4px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-orders__skeleton-time {
  width: 120px;
  height: 14px;
  border-radius: 4px;
  margin-bottom: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

.m-orders__skeleton-actions {
  display: flex;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #f5f5f5;
}

.m-orders__skeleton-btn {
  width: 72px;
  height: 24px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

/* ====== 列表 ====== */
.m-orders__list {
  padding: 12px;
}

.m-orders__card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.m-orders__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.m-orders__card-no {
  font-size: 13px;
  color: #666;
}

.m-orders__card-body {
  margin-bottom: 12px;
}

.m-orders__card-product {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.m-orders__card-price {
  font-size: 20px;
  font-weight: 700;
  color: #FF4D4F;
  margin-bottom: 4px;
}

.m-orders__card-time {
  font-size: 12px;
  color: #999;
}

.m-orders__card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #f5f5f5;
}

/* 发票弹窗 */
.m-orders__invoice {
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
}

.m-orders__invoice-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin-bottom: 16px;
}

.m-orders__invoice-section {
  padding: 12px 16px;
}

.m-orders__invoice-label {
  font-size: 14px;
  color: #646566;
  margin-bottom: 8px;
}

.m-orders__radio-group {
  display: flex;
  gap: 20px;
}

.m-orders__radio-group ::v-deep .van-radio {
  align-items: center;
}

.m-orders__radio-group ::v-deep .van-radio__icon {
  font-size: 18px;
}

.m-orders__radio-group ::v-deep .van-radio__icon--round .van-icon {
  font-size: 18px;
}

.m-orders__radio-group ::v-deep .van-radio__label {
  font-size: 14px;
  color: #333;
  margin-left: 6px;
  line-height: 20px;
}

.m-orders__invoice-audit {
  margin: 12px 16px;
  padding: 10px 12px;
  background: #FFF1F0;
  border-radius: 8px;
  font-size: 13px;
  color: #FF4D4F;
  line-height: 1.8;
}

.m-orders__invoice-actions {
  padding: 16px;
}

/* 支付弹窗 */
.m-orders__pay {
  padding: 24px 20px;
  text-align: center;
}

.m-orders__pay-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.m-orders__pay-info {
  margin-bottom: 24px;
}

.m-orders__pay-product {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.m-orders__pay-amount {
  font-size: 28px;
  font-weight: 700;
  color: #FF4D4F;
  margin-bottom: 8px;
}

.m-orders__pay-no {
  font-size: 12px;
  color: #999;
}

.m-orders__pay-success {
  padding: 20px 0;
}

.m-orders__pay-success p {
  font-size: 14px;
  color: #666;
  margin-top: 12px;
}
</style>
